import React, { useEffect, useMemo, useState } from "react";
import { ArrayProvider, useArrayContext } from "../../../state/ArrayContext";
import { findFirstComponentOfType } from "../../internal/TextExtractor";
import { SourceItem } from "../../../shared/models/source-item.model";

interface FilterArrayProps {
    children: React.ReactNode;
}

/***
 * Array transformer that filters an array of values based on the keep and remove array sources.
 * The keep and remove arrays are optional and if not provided, all values will be kept or removed respectively.
 * The input arrays come from array source components which are children of the filter array component.
 */
export const FilterArray = React.memo(function FilterArray({ children }: FilterArrayProps): React.ReactElement {
    const { setArray } = useArrayContext();
    // Get the source child which should contain an array source with values to filter
    const sourceChild = useMemo(() => findFirstComponentOfType(children, "arg-source"), [children]);
    const [sourceArray, setSourceArray] = useState<Array<SourceItem>>();

    // Get the keep child which should contain an array source with values to keep
    const keepChild = useMemo(() => findFirstComponentOfType(children, "arg-keep"), [children]);
    const [keepArray, setKeepArray] = useState<Array<SourceItem>>();

    // Get the remove child which should contain an array source with values to remove
    const removeChild = useMemo(() => findFirstComponentOfType(children, "arg-remove"), [children]);
    const [removeArray, setRemoveArray] = useState<Array<SourceItem>>();

    useEffect(() => {
        if (sourceArray) {
            const items = sourceArray
                .filter(item =>
                    (!keepArray || keepArray?.some(keepItem => keepItem.value === item.value)) &&
                    (!removeArray || !removeArray?.some(removeItem => removeItem.value === item.value)))
                .map((item, ix) => ({ ...item, position: ix + 1 }));
            setArray(items);
        }
    }, [sourceArray, keepArray, removeArray, setArray]);

    return <>
        {useMemo(() => {
            return <ArrayProvider setArray={setSourceArray}>
                {sourceChild?.props.children}
            </ArrayProvider>
        }, [sourceChild, setSourceArray])}
        {useMemo(() => {
            return <ArrayProvider setArray={setKeepArray}>
                {keepChild?.props.children}
            </ArrayProvider>
        }, [keepChild, setKeepArray])}
        {useMemo(() => {
            return <ArrayProvider setArray={setRemoveArray}>
                {removeChild?.props.children}
            </ArrayProvider>
        }, [removeChild, setRemoveArray])}
    </>;
});